// Shared navigation ownership decision for shell and storefront headers.
//
// Inputs: an internal href, merchant capabilities, and the migration cookies
// written by proxy.ts after a zone handoff. Output: whether the href crosses a
// repository ownership boundary and therefore needs a full browser navigation.
// Keep route boundaries and cookie names aligned with the proxy/next.config
// rewrite contracts; merchant flags only constrain which zone can own a path.

import type { Merchant } from "@/dto/merchant";
import {
  NON_RETAIL_ROUTES,
  ROUTE_CATEGORIES,
  ROUTE_MENU,
  ROUTE_SERVICES,
  ROUTE_SHOP,
  RETAIL_MIGRATION_COOKIE_KEY,
  RESTAURANT_MIGRATION_COOKIE_KEY,
  SERVICES_MIGRATION_COOKIE_KEY,
} from "@/utils/constants";

const hasMigrationCookie = (key: string): boolean => {
  if (typeof document === "undefined") return false;

  return document.cookie.split(";").some((cookie) => {
    const [name, ...parts] = cookie.trim().split("=");
    return name === key && parts.join("=") === "1";
  });
};

export const getHardNavigationHref = (href: string | object): string => {
  if (typeof href === "string") {
    return href;
  }

  if ("pathname" in href && typeof href.pathname === "string") {
    const query =
      "query" in href && href.query && typeof href.query === "object"
        ? new URLSearchParams(
            Object.entries(href.query).flatMap(([key, value]) =>
              Array.isArray(value)
                ? value.map((item) => [key, String(item)])
                : [[key, String(value)]]
            )
          ).toString()
        : "";
    const hash = "hash" in href && typeof href.hash === "string" ? href.hash : "";

    return href.pathname + (query ? "?" + query : "") + hash;
  }

  return "/";
};

const getPathname = (href: string | object): string =>
  getHardNavigationHref(href).split(/[?#]/, 1)[0] || "/";

const hasPathPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isRetailZonePath = (pathname: string): boolean => {
  if (pathname === "/") return false;

  return !NON_RETAIL_ROUTES.some(
    (reservedSegment) =>
      hasPathPrefix(pathname, `/${reservedSegment.toLowerCase()}`)
  );
};

/**
 * Returns true only when the target path is currently owned by a migrated zone.
 * This intentionally does not infer ownership from `merchant.isRetail` alone:
 * a merchant can be retail while selected routes remain shell-owned.
 */
export const shouldHardNavigate = (
  href: string | object,
  merchant: Partial<Pick<
    Merchant,
    | "isRetail"
    | "isService"
    | "isRestaurant"
    | "isHybrid"
    | "isEssentialMerchant"
    | "hasOnlyRetailItems"
  >>
): boolean => {
  const pathname = getPathname(href);

  if (
    hasPathPrefix(pathname, ROUTE_SERVICES) &&
    (merchant.isService || merchant.isHybrid)
  ) {
    return hasMigrationCookie(SERVICES_MIGRATION_COOKIE_KEY);
  }

  if (
    merchant.isRestaurant &&
    hasPathPrefix(
      pathname,
      ROUTE_MENU(
        hasMigrationCookie(RESTAURANT_MIGRATION_COOKIE_KEY)
      )
    )
  ) {
    return hasMigrationCookie(RESTAURANT_MIGRATION_COOKIE_KEY);
  }

  const shopOnlyMerchant =
    merchant.isHybrid || (merchant.isService && merchant.hasOnlyRetailItems);
  const retailEligible =
    merchant.isRetail || merchant.isEssentialMerchant || shopOnlyMerchant;
  const retailPath = shopOnlyMerchant
    ? hasPathPrefix(pathname, ROUTE_SHOP)
    : merchant.isEssentialMerchant
      ? hasPathPrefix(pathname, ROUTE_CATEGORIES)
      : isRetailZonePath(pathname);

  if (
    retailEligible &&
    retailPath
  ) {
    return hasMigrationCookie(RETAIL_MIGRATION_COOKIE_KEY);
  }

  return false;
};
